import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Tag, Timeline, Button, Spin, message, theme } from 'antd';
import { 
  ArrowLeftOutlined, 
  CalendarOutlined, 
  UserOutlined, 
  ProjectOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { apiService } from '../../services/api';
import { ScopeAcknowledgementBanner } from '../../components/ScopeAcknowledgement';
import { ScopeVersionHistory } from '../../components/ScopeVersionHistory';
import { WizardStatus } from '../../components/WizardStatus';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';
import { CitadeloLayout } from '../../components/CitadeloLayout';

const { Title, Text } = Typography;

interface ProjectType {
  id: number;
  name: string;
  zohoValue?: string;
  category?: string;
}

interface Project {
  id: number;
  pid: string;
  projectName?: string;
  client?: {
    name: string;
    contactFirstName?: string;
    contactLastName?: string;
    contactName?: string;
  };
  projectTypes?: ProjectType[];
  customProjectType?: string;
  projectStatus?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  retestDate?: string;
  scope: string;
  pendingScopeAcknowledgement?: boolean;
  methodology?: Array<{
    id: number;
    title: string;
    description: string;
    completed: boolean;
    notes?: string;
  }>;
}

const statusColors: Record<string, string> = {
  proposal: 'gold',
  obtaining: 'default',
  preparing: 'processing',
  scheduled: 'cyan',
  testing: 'warning',
  reporting: 'processing',
  follow_up: 'success',
  completed: 'success'
};

const statusLabels: Record<string, string> = {
  proposal: 'Proposal',
  obtaining: 'Obtaining',
  preparing: 'Preparing',
  scheduled: 'Scheduled',
  testing: 'Testing',
  reporting: 'Reporting',
  follow_up: 'Follow-up',
  completed: 'Completed'
};

// Helper function to get display name for project types
const getProjectTypesDisplay = (project: Project): string[] => {
  const types: string[] = [];
  if (project.projectTypes && project.projectTypes.length > 0) {
    types.push(...project.projectTypes.map(pt => pt.name));
  }
  if (project.customProjectType) {
    types.push(project.customProjectType);
  }
  return types.length > 0 ? types : ['Not specified'];
};

// Helper to get contact name
const getContactName = (client?: Project['client']): string => {
  if (!client) return '';
  if (client.contactFirstName || client.contactLastName) {
    return `${client.contactFirstName || ''} ${client.contactLastName || ''}`.trim();
  }
  return client.contactName || '';
};

export const ProjectStatus: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const { token } = theme.useToken();
  const { isDarkMode } = useTheme();
  const { logout } = useAuth();

  const fetchProject = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getProjectById(projectId!);
      setProject(response.data);
    } catch (error) {
      message.error('Failed to load project information');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    // Always fetch fresh project data to ensure we have the latest status
    if (projectId) {
      fetchProject();
    }
  }, [projectId, fetchProject]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusTimeline = () => {
    const statuses = ['proposal', 'preparing', 'scheduled', 'testing', 'reporting', 'follow_up', 'completed'];
    const currentStatus = project?.projectStatus || project?.status || 'proposal';
    // Map legacy 'obtaining' to 'proposal'
    const normalizedStatus = currentStatus === 'obtaining' ? 'proposal' : currentStatus;
    const currentIndex = statuses.indexOf(normalizedStatus);
    
    return statuses.map((status, index) => ({
      color: index <= currentIndex ? 'green' : 'gray',
      children: (
        <div>
          <Text strong>{statusLabels[status]}</Text>
          {index === currentIndex && (
            <Tag color={statusColors[status]} style={{ marginLeft: '8px' }}>
              Current
            </Tag>
          )}
        </div>
      )
    }));
  };

  if (loading) {
    return (
      <CitadeloLayout onLogout={logout} onRefresh={fetchProject}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text>Loading project status...</Text>
          </div>
        </div>
      </CitadeloLayout>
    );
  }

  if (!project) {
    return (
      <CitadeloLayout onLogout={logout}>
        <Card>
          <Text type="danger">Project not found</Text>
        </Card>
      </CitadeloLayout>
    );
  }

  return (
    <CitadeloLayout onLogout={logout} onRefresh={fetchProject}>
      <div>
        {/* Scope Acknowledgement Banner */}
        {project.pendingScopeAcknowledgement && (
          <ScopeAcknowledgementBanner
            projectId={project.id}
            pendingAcknowledgement={project.pendingScopeAcknowledgement}
            onAcknowledged={fetchProject}
          />
        )}

        {/* Header */}
        <Card style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <Title level={2} style={{ margin: 0 }}>
                <ProjectOutlined style={{ marginRight: '8px' }} />
                Project Status
              </Title>
              <Text type="secondary">
                {project.client?.name} - {project.pid}
              </Text>
            </div>
            <Button onClick={() => navigate('/projects')} icon={<ArrowLeftOutlined />}>
              Back to Projects
            </Button>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Tag color={statusColors[project.projectStatus || project.status || 'proposal']} style={{ fontSize: '14px', padding: '4px 12px' }}>
              {statusLabels[project.projectStatus || project.status || 'proposal']}
            </Tag>
            {getProjectTypesDisplay(project).map((type, idx) => (
              <Tag key={idx} color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                {type}
              </Tag>
            ))}
          </div>
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Project Details */}
          <Card title="Project Details">
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Client Information</Text>
              <div style={{ marginTop: '8px' }}>
                <Text>
                  <UserOutlined style={{ marginRight: '8px' }} />
                  {getContactName(project.client) || 'Contact not specified'}
                </Text>
                <br />
                <Text type="secondary">{project.client?.name}</Text>
              </div>
            </div>

            {project.projectName && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Project Name</Text>
                <div style={{ marginTop: '8px' }}>
                  <Text>{project.projectName}</Text>
                </div>
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Timeline</Text>
              <div style={{ marginTop: '8px' }}>
                <Text>
                  <CalendarOutlined style={{ marginRight: '8px' }} />
                  Start Date: {formatDate(project.startDate)}
                </Text>
                <br />
                <Text>
                  <CalendarOutlined style={{ marginRight: '8px' }} />
                  End Date: {formatDate(project.endDate)}
                </Text>
                {project.retestDate && (
                  <>
                    <br />
                    <Text>
                      <CalendarOutlined style={{ marginRight: '8px' }} />
                      Retest Date: {formatDate(project.retestDate)}
                    </Text>
                  </>
                )}
              </div>
            </div>

            <div>
              <Text strong>Project Scope</Text>
              <div style={{ 
                marginTop: '8px', 
                padding: '12px', 
                background: isDarkMode ? token.colorBgElevated : token.colorBgLayout, 
                borderRadius: '4px',
                maxHeight: '200px',
                overflow: 'auto',
                border: `1px solid ${token.colorBorder}`
              }}>
                <Text type="secondary">
                  {project.scope ? project.scope.replace(/<[^>]*>/g, '') : 'No scope defined'}
                </Text>
              </div>
            </div>
          </Card>

          {/* Status Timeline */}
          <Card title="Project Progress">
            <Timeline items={getStatusTimeline()} />
          </Card>
        </div>

        {/* Wizard Status */}
        <div style={{ marginTop: '24px' }}>
          <WizardStatus 
            projectId={project.id} 
            projectStatus={project.projectStatus || project.status}
          />
        </div>

        {/* Methodology */}
        {project.methodology && project.methodology.length > 0 && (
          <Card title="Methodology Checklist" style={{ marginTop: '24px' }}>
            <div style={{ display: 'grid', gap: '12px' }}>
              {project.methodology.map((item) => (
                <div key={item.id} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  padding: '12px', 
                  border: `1px solid ${token.colorBorder}`, 
                  borderRadius: '4px',
                  background: item.completed 
                    ? (isDarkMode ? 'rgba(82, 196, 26, 0.1)' : '#f6ffed') 
                    : token.colorBgContainer
                }}>
                  {item.completed ? (
                    <CheckCircleOutlined style={{ color: token.colorSuccess, marginRight: '12px' }} />
                  ) : (
                    <ClockCircleOutlined style={{ color: token.colorTextDisabled, marginRight: '12px' }} />
                  )}
                  <div style={{ flex: 1 }}>
                    <Text strong>{item.title}</Text>
                    {item.description && (
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {item.description}
                        </Text>
                      </div>
                    )}
                    {item.notes && (
                      <div style={{ marginTop: '4px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Notes: {item.notes}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Scope Version History - Clients can propose changes */}
        <div style={{ marginTop: '24px' }}>
          <ScopeVersionHistory
            projectId={project.id}
            currentScope={project.scope}
            readOnly={true}
            canPropose={true}
            isClientView={true}
          />
        </div>
      </div>
    </CitadeloLayout>
  );
};
